import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

export const fetchCanvass = createAsyncThunk(
  "canvass/fetchCanvass",
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(`${BASE_URL}/v1/canvass`, {
        params: {
          skip,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return {
        canvasses: response.data.data,
        total: response.data.data.length,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: { requiresNetwork: true },
  }
);

// Fetch single canvass
export const fetchCanvassByAny = createAsyncThunk(
  "canvass/fetchCanvassByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/canvass/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.data[0];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: { requiresNetwork: true },
  }
);

const initialState = {
  canvasses: [],
  currentCanvass: null,
  total: 0,
  isLoading: false,
  error: null,
  success: false,
  isOfflineData: false,
};

const canvassSlice = createSlice({
  name: "canvass",
  initialState,
  reducers: {
    resetCanvassState: () => initialState,
    setOfflineMode: (state, action) => {
      state.isOfflineData = action.payload;
    },
    loadCachedData: (state, action) => {
      return {
        ...state,
        ...action.payload,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCanvass.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCanvass.fulfilled, (state, action) => {
        state.isLoading = false;
        state.canvasses = action.payload.canvasses;
        state.total = action.payload.total;
        state.error = null;
      })
      .addCase(fetchCanvass.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCanvassByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCanvassByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCanvass = action.payload;
        state.error = null;
      })
      .addCase(fetchCanvassByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const { resetCanvassState, setOfflineMode, loadCachedData } =
  canvassSlice.actions;

// Export selectors
export const selectAllCanvass = (state) => state.canvass.canvasses;
export const selectCurrentCanvass = (state) => state.canvass.currentCanvass;
export const selectCanvassLoading = (state) => state.canvass.isLoading;
export const selectCanvassError = (state) => state.canvass.error;
export const selectCanvassTotal = (state) => state.canvass.total;
export const selectIsOfflineData = (state) =>
  state.canvass.isOfflineData || false;

export default canvassSlice.reducer;
